import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../api/axios";

import SelectionHeader from "../../components/selection/SelectionHeader";
import AvailableList from "../../components/selection/AvailableList";
import SelectedList from "../../components/selection/SelectedList";

/**
 * Generic Squad Selection Page
 *
 * Used by:
 * - Competition → Squad
 * - Series → Squad
 *
 * Props:
 * - title: string
 * - teamsEndpoint: "/api/competitions/:id/teams/" | "/api/series/:id/teams/"
 * - squadEndpoint: "/api/competition-teams/:id/squad/"
 */
export default function SquadSelection({
  title,
  teamsEndpoint,
  squadEndpoint,
}) {
  const { id } = useParams();
  const navigate = useNavigate();

  const [teams, setTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState("");

  const [availableItems, setAvailableItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [initialSelectedItems, setInitialSelectedItems] = useState([]);

  const [loading, setLoading] = useState(true);
  const [loadingSquad, setLoadingSquad] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");

  const buildUrl = (endpoint, params) =>
    endpoint.replace(/:([a-zA-Z]+)/g, (_, key) => {
      if (!(key in params)) {
        throw new Error(`Missing param: ${key}`);
      }
      return params[key];
    });

  // Normalise list responses — backends here can return either a bare
  // array, {teams: []}, {players: []}, OR a DRF-paginated
  // {count, next, previous, results: []}. Without this, setState
  // sometimes lands an OBJECT in a slot the render expects to be an
  // array, then `.filter is not a function` crashes the page.
  const toArray = (data, ...keys) => {
    if (Array.isArray(data)) return data;
    if (!data || typeof data !== "object") return [];
    for (const k of keys) {
      if (Array.isArray(data[k])) return data[k];
    }
    if (Array.isArray(data.results)) return data.results;
    return [];
  };

  // Load teams for competition / series
  useEffect(() => {
    async function loadTeams() {
      try {
        const res = await api.get(teamsEndpoint.replace(":id", id));
        setTeams(toArray(res.data, "teams"));
      } catch (err) {
        alert(err?.response?.data?.detail || "Failed to load teams");
      } finally {
        setLoading(false);
      }
    }

    loadTeams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, teamsEndpoint]);

  // Load squad when team changes
  useEffect(() => {
    if (!selectedTeamId) return;

    async function loadSquad() {
      setLoadingSquad(true);
      try {
        const squadUrl = buildUrl(squadEndpoint, {
          id: id,
          tid: selectedTeamId,
        });
        const [squadRes, playersRes] = await Promise.all([
          api.get(squadUrl),
          api.get("/api/coredata/my-players/"),
        ]);

        const players = toArray(squadRes.data, "players");
        setSelectedItems(players);
        setInitialSelectedItems(players);
        setAvailableItems(toArray(playersRes.data, "players"));
      } catch (err) {
        alert(err?.response?.data?.detail || "Failed to load squad");
      } finally {
        setLoadingSquad(false);
      }
    }

    loadSquad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTeamId, squadEndpoint, id]);

  const toggleItem = (item) => {
    setSelectedItems((prev) =>
      prev.some((p) => p.id === item.id)
        ? prev.filter((p) => p.id !== item.id)
        : [...prev, item],
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const squadUrl = buildUrl(squadEndpoint, {
        id: id,
        tid: selectedTeamId,
      });
      await api.post(squadUrl, {
        player_ids: selectedItems.map((p) => p.id),
      });
      // Update initial state to reflect saved changes
      setInitialSelectedItems(selectedItems);
    } catch (err) {
      alert(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const isDirty =
    JSON.stringify(selectedItems) !== JSON.stringify(initialSelectedItems);

  if (loading) {
    return (
      <div className="min-h-screen bg-bg-main flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-button-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-text-secondary">Loading teams...</p>
        </div>
      </div>
    );
  }

  // Defensive — even with the toArray normalisation above, render must
  // never crash if availableItems somehow becomes non-array (e.g. an
  // ErrorBoundary-triggered re-render with stale state).
  const filteredAvailable = (Array.isArray(availableItems) ? availableItems : []).filter((p) =>
    `${p.first_name || ""} ${p.name || ""}`
      .toLowerCase()
      .includes(search.toLowerCase()),
  );

  return (
    <div className="min-h-screen bg-bg-main">
      {/* Header */}
      <div className="bg-bg-primary border-b border-bg-secondary shadow-lg sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(-1)}
                className="p-2 rounded-lg bg-bg-secondary hover:bg-bg-main transition-colors"
              >
                <svg
                  className="w-5 h-5 text-text-secondary"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 19l-7-7m0 0l7-7m-7 7h18"
                  />
                </svg>
              </button>
              <div>
                <h1 className="text-2xl font-bold text-text-primary">
                  {title}
                </h1>
                <p className="text-sm text-text-secondary">
                  Select team and manage squad
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {isDirty && selectedTeamId && (
                <div className="flex items-center gap-2 px-3 py-2 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                  <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse"></div>
                  <span className="text-sm text-amber-400">
                    Unsaved changes
                  </span>
                </div>
              )}

              <button
                onClick={() => navigate(-1)}
                className="px-4 py-2 bg-bg-secondary text-text-secondary rounded-lg hover:bg-bg-main transition-colors"
              >
                Cancel
              </button>

              <button
                onClick={handleSave}
                disabled={!isDirty || !selectedTeamId || saving}
                className="px-6 py-2 rounded-lg font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                style={{
                  backgroundColor:
                    isDirty && selectedTeamId ? "#6B1991" : undefined,
                  color: isDirty && selectedTeamId ? "#FFFFFF" : undefined,
                }}
              >
                {saving ? (
                  <>
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Saving...
                  </>
                ) : (
                  <>
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto p-6">
        {/* Team Selector */}
        <div className="bg-bg-primary rounded-xl p-6 mb-6 border border-bg-secondary shadow-lg">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-lg flex items-center justify-center flex-shrink-0 border border-blue-500/30">
              <svg
                className="w-6 h-6 text-blue-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
            </div>
            <div className="flex-1">
              <label className="block text-sm text-text-secondary mb-2">
                Select Team <span className="text-red-400">*</span>
              </label>
              <select
                value={selectedTeamId}
                onChange={(e) => setSelectedTeamId(e.target.value)}
                className="w-full p-3 px-4 rounded-lg bg-bg-main text-text-primary border border-bg-secondary focus:border-button-primary outline-none transition-colors"
              >
                <option value="">-- Choose a team --</option>
                {teams.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
              <p className="text-xs text-text-secondary mt-2">
                Select a team to view and manage its squad
              </p>
            </div>
          </div>
        </div>

        {/* Loading Squad State */}
        {loadingSquad && (
          <div className="text-center py-16">
            <div className="w-16 h-16 border-4 border-button-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-text-secondary">Loading squad...</p>
          </div>
        )}

        {/* No Team Selected State */}
        {!selectedTeamId && !loadingSquad && (
          <div className="text-center py-16">
            <div className="w-20 h-20 bg-bg-primary rounded-full flex items-center justify-center mx-auto mb-4 border border-bg-secondary">
              <svg
                className="w-10 h-10 text-text-secondary"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-text-primary mb-2">
              Select a Team
            </h3>
            <p className="text-text-secondary">
              Choose a team from the dropdown above to manage its squad
            </p>
          </div>
        )}

        {/* Stats Bar */}
        {selectedTeamId && !loadingSquad && (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              <div className="bg-bg-primary rounded-lg p-4 border border-bg-secondary">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-text-secondary mb-1">
                      Available Players
                    </p>
                    <p className="text-2xl font-bold text-text-primary">
                      {availableItems.length}
                    </p>
                  </div>
                  <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                    <svg
                      className="w-5 h-5 text-blue-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                      />
                    </svg>
                  </div>
                </div>
              </div>

              <div className="bg-bg-primary rounded-lg p-4 border border-bg-secondary">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-text-secondary mb-1">
                      Squad Size
                    </p>
                    <p className="text-2xl font-bold text-text-primary">
                      {selectedItems.length}
                    </p>
                  </div>
                  <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                    <svg
                      className="w-5 h-5 text-purple-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                </div>
              </div>

              <div className="bg-bg-primary rounded-lg p-4 border border-bg-secondary">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-text-secondary mb-1">
                      Changes Made
                    </p>
                    <p className="text-2xl font-bold text-text-primary">
                      {Math.abs(
                        selectedItems.length - initialSelectedItems.length,
                      )}
                    </p>
                  </div>
                  <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                    <svg
                      className="w-5 h-5 text-emerald-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            {/* Selection Grid */}
            <div className="grid md:grid-cols-2 gap-6">
              <AvailableList
                items={filteredAvailable}
                selectedItems={selectedItems}
                onToggle={toggleItem}
                search={search}
                onSearch={setSearch}
              />

              <SelectedList items={selectedItems} onRemove={toggleItem} />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
